import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { ApiError, unwrap } from '@/api/envelope';
import { updateDocument, type DocumentUpdate } from '@/api/generated';
import { applyServerErrors } from '@/api/serverErrors';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { FormSkeleton } from '@/components/ui/Skeletons';
import { StatusChip } from '@/components/ui/StatusChip';
import { useToast } from '@/components/ui/useToast';
import { formatDate } from '@/lib/organization';
import { testIdAttr } from '@/lib/testIds';

import { CategorySelectField } from './CategorySelectField';
import { DocumentCount } from './DocumentCount';
import { RemoveDocumentDialog } from './RemoveDocumentDialog';
import { ReplaceFileDialog } from './ReplaceFileDialog';
import {
  ACTION_LABELS,
  TRANSITIONS,
  actionsFor,
  type DocumentActionId,
} from './documentActions';
import {
  sizeInWords,
  stateTone,
  stateWord,
  typeWord,
  type DocumentRow,
} from './documentLabels';
import {
  DOCUMENT_DESCRIPTION_MAX,
  DOCUMENT_TITLE_MAX,
  documentFormSchema,
} from './documentSchemas';
import { documentFieldRefusal } from './documentRefusals';
import { useDocumentDetail, useInvalidateDocuments } from './useDocuments';
import { useDocumentLink } from './useDocumentLink';
import { useDocumentTransition } from './useDocumentWrites';

/** The shared fields plus the publish request, which only the drawer edits. */
const drawerSchema = documentFormSchema.extend({ publishRequested: z.boolean() });
type DrawerInput = z.input<typeof drawerSchema>;
type DrawerValues = z.output<typeof drawerSchema>;

/** The server's field names to the form's, in page order. */
const SERVER_FIELDS = {
  title: 'title',
  description: 'description',
  category_id: 'categoryId',
  publish_requested: 'publishRequested',
} as const;

interface EditDocumentDrawerProps {
  /** The document shown, or null when the drawer is closed. */
  id: string | null;
  /** The days the views and downloads cover, as the library shows them. */
  range: number;
  /** Whether the person may change documents (P15); the page decides. */
  writable: boolean;
  onClose: () => void;
}

/** What the form holds for a row. */
function valuesOf(row: DocumentRow): DrawerValues {
  return {
    title: row.title,
    description: row.description ?? '',
    categoryId: row.category?.id ?? '',
    publishRequested: row.publishRequested,
  };
}

/** A label and its value. */
function Fact({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography component="dt" variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        data-testid={testId}
        sx={{ m: 0, overflowWrap: 'anywhere' }}
      >
        {children}
      </Typography>
    </Box>
  );
}

/**
 * The edit drawer (PB6): the editable fields, the facts, and the actions the
 * row offers - the same table as the row menu (P11), with Edit itself being
 * this drawer.
 *
 * It reads its own detail query, which is the only reply carrying the pending
 * replacement, and polls on the same rule as the list (P10). Save sends only
 * what changed and stays open, so a person can go on to publish or replace;
 * Remove is the one action that closes it, because the row is then read-only.
 * The activity drawer's shape: a temporary drawer that holds focus, starts on
 * its heading, and gives focus back to the row's menu button on close.
 */
export function EditDocumentDrawer({
  id,
  range,
  writable,
  onClose,
}: EditDocumentDrawerProps) {
  const open = id !== null;
  const headingId = useId();
  const descriptionId = useId();
  const formId = `${headingId}-form`;
  const invalidate = useInvalidateDocuments();
  const toast = useToast();

  const detail = useDocumentDetail(id, range);
  const row = detail.data ?? null;
  const transition = useDocumentTransition();
  const link = useDocumentLink();

  const [failure, setFailure] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [replaceCount, setReplaceCount] = useState(0);
  const [removing, setRemoving] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);

  // The form follows the detail: a reset when it first arrives and after each
  // poll, keeping whatever is being typed (RHF's `values` with keepDirtyValues).
  const values = useMemo(() => (row ? valuesOf(row) : undefined), [row]);
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setFocus,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<DrawerInput, unknown, DrawerValues>({
    resolver: zodResolver(drawerSchema),
    values,
    resetOptions: { keepDirtyValues: true },
    defaultValues: {
      title: '',
      description: '',
      categoryId: '',
      publishRequested: false,
    },
  });

  const titleTyped = useWatch({ control, name: 'title' }).length;
  const descriptionTyped = useWatch({ control, name: 'description' }).length;

  useEffect(() => {
    if (failure) alertRef.current?.focus();
  }, [failure]);

  const save = useMutation({
    mutationFn: async (next: DrawerValues) => {
      // Only what changed, so the backend's audit entry names just that.
      const body: DocumentUpdate = {};
      if (dirtyFields.title) body.title = next.title;
      if (dirtyFields.description) body.description = next.description || null;
      if (dirtyFields.categoryId) body.category_id = next.categoryId || null;
      if (dirtyFields.publishRequested) body.publish_requested = next.publishRequested;
      const reply = await updateDocument({
        path: { document_id: id as string },
        body,
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFailure(null),
    onSuccess: async (saved) => {
      // Clean against what was saved; the refetch then brings the same values.
      reset(
        valuesOf({
          ...(row as DocumentRow),
          title: saved.title,
          description: saved.description ?? null,
          category: saved.category
            ? { id: saved.category.id, name: saved.category.name }
            : null,
          publishRequested: saved.publish_requested,
        }),
      );
      toast.show({ message: 'Saved.' });
      // Save is disabled once clean, which would drop focus out of the drawer
      // (and Escape with it); the heading keeps it inside.
      headingRef.current?.focus();
      await invalidate();
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) {
        // The row on screen was wrong (a removed document): said, and the
        // library asked for again.
        toast.show({ message: error.serverMessage ?? error.message, severity: 'error' });
        await invalidate();
        return;
      }
      if (error instanceof ApiError && error.status === 403) {
        // Said here, with the edits kept (error contract: 403 is inline), and
        // the library asked for again so the offered actions are reviewed.
        setFailure(error.message);
        await invalidate();
        return;
      }
      const refused = documentFieldRefusal(error);
      if (refused) {
        setError(refused.field, { type: 'server', message: refused.message });
        setFocus(refused.field);
        return;
      }
      const { remainder } = applyServerErrors(
        error,
        { setError, setFocus },
        SERVER_FIELDS,
      );
      if (remainder) setFailure(remainder);
    },
  });

  // Leaving the page with edits asks; so does closing the drawer with them.
  const guard = useUnsavedChanges(isDirty && open);
  const askToClose = () => {
    if (save.isPending || transition.isPending) return;
    if (isDirty) setConfirmingClose(true);
    else onClose();
  };

  /*
   * Focus goes to the heading when the drawer opens, so a screen reader
   * starts from what this is. Done as the heading arrives rather than in an
   * effect: the drawer renders its contents a step after it opens.
   */
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const focusOnArrival = useCallback((node: HTMLHeadingElement | null) => {
    headingRef.current = node;
    node?.focus();
  }, []);

  function act(action: DocumentActionId) {
    if (!row) return;
    const to = TRANSITIONS[action];
    if (to) transition.mutate({ row, to });
    else if (action === 'open') void link.open(row.id);
    else if (action === 'download') void link.download(row.id);
    else if (action === 'replace') {
      setReplaceCount((n) => n + 1);
      setReplacing(true);
    } else if (action === 'remove') setRemoving(true);
  }

  const actions = row
    ? actionsFor(row, writable).filter((action) => action !== 'edit')
    : [];
  // A removed document is read-only (backend I2), and so is everything for a
  // person who may not write: the fields show, nothing can be saved.
  const readOnly = !writable || row?.lifecycle === 'ARCHIVED';
  const showPublishSwitch =
    row?.lifecycle === 'DRAFT' &&
    row.scanStatus === 'PENDING' &&
    row.pendingFile === null;
  const busy = save.isPending || transition.isPending;
  const notFound = detail.error instanceof ApiError && detail.error.status === 404;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={askToClose}
      // The heading takes focus instead of the drawer's own container; focus
      // is still held inside while open, and goes back to the opener on close.
      ModalProps={{ disableAutoFocus: true }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': headingId,
          'aria-describedby': descriptionId,
          ...testIdAttr('document-drawer'),
          sx: { width: { xs: '100%', sm: 480 }, p: 3 },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <Typography
          id={headingId}
          ref={focusOnArrival}
          tabIndex={-1}
          variant="h6"
          component="h2"
          data-testid="document-drawer-title"
          sx={{ flex: 1, '&:focus': { outline: 'none' } }}
        >
          Edit document
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={askToClose}
          edge="end"
          data-testid="document-drawer-close-button"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography
        id={descriptionId}
        variant="body2"
        color="text.secondary"
        data-testid="document-drawer-description"
        sx={{ mb: 2, overflowWrap: 'anywhere' }}
      >
        {row
          ? `“${row.title}”: change its details, publish or unpublish it, replace its file or remove it.`
          : 'Loading the document.'}
      </Typography>

      {detail.isError ? (
        <ErrorState
          title={
            notFound
              ? 'This document no longer exists.'
              : 'The document could not be loaded.'
          }
          onRetry={notFound ? undefined : () => void detail.refetch()}
          testId="document-drawer"
        />
      ) : !row ? (
        <Box data-testid="document-drawer-loading">
          <FormSkeleton fields={3} label="Loading the document" />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <StatusChip
              label={stateWord(row)}
              tone={stateTone(row)}
              testId="document-drawer-state-chip"
            />
            {row.pendingFile ? (
              <Typography variant="caption" color="text.secondary">
                Replacement scanning
              </Typography>
            ) : null}
          </Box>

          <Stack
            component="form"
            id={formId}
            spacing={2.5}
            aria-label="Document details"
            data-testid="document-drawer-form"
            noValidate
            onSubmit={(event) => {
              void handleSubmit((next) => save.mutate(next))(event);
            }}
          >
            {failure ? (
              <Alert
                severity="error"
                role="alert"
                ref={alertRef}
                tabIndex={-1}
                data-testid="document-drawer-save-error"
              >
                {failure}
              </Alert>
            ) : null}

            <Field
              label="Title"
              hint={`${titleTyped} of ${DOCUMENT_TITLE_MAX} characters`}
              error={errors.title?.message}
              testId="document-drawer-field-title"
            >
              <TextField
                {...register('title')}
                size="small"
                fullWidth
                disabled={readOnly}
              />
            </Field>

            <Field
              label="Description"
              hint={`${descriptionTyped} of ${DOCUMENT_DESCRIPTION_MAX} characters`}
              error={errors.description?.message}
              testId="document-drawer-field-description"
            >
              <TextField
                {...register('description')}
                size="small"
                fullWidth
                multiline
                minRows={2}
                disabled={readOnly}
              />
            </Field>

            <Controller
              name="categoryId"
              control={control}
              render={({ field }) => (
                <CategorySelectField
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  inputRef={field.ref}
                  error={errors.categoryId?.message}
                  disabled={readOnly}
                  testId="document-drawer-field-category"
                />
              )}
            />

            {showPublishSwitch && !readOnly ? (
              <Controller
                name="publishRequested"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                        slotProps={{
                          input: {
                            role: 'switch',
                            ...testIdAttr('document-drawer-publish-on-clean-switch'),
                          },
                        }}
                      />
                    }
                    label="Publish once the scan is clean"
                  />
                )}
              />
            ) : null}

            {readOnly ? null : (
              <Box>
                <Button
                  type="submit"
                  disabled={!isDirty || busy}
                  loading={save.isPending}
                  data-testid="document-drawer-save-button"
                >
                  Save
                </Button>
              </Box>
            )}
          </Stack>

          <Typography variant="subtitle2" component="h3" sx={{ mt: 3, mb: 1 }}>
            File
          </Typography>
          <Box component="dl" sx={{ m: 0 }}>
            <Fact label="File name" testId="document-drawer-file-name-value">
              {row.fileName}
            </Fact>
            <Fact label="Type" testId="document-drawer-type-value">
              {typeWord(row.contentType)}
            </Fact>
            <Fact label="Size" testId="document-drawer-size-value">
              {sizeInWords(row.sizeBytes)}
            </Fact>
            <Fact label="Uploaded" testId="document-drawer-uploaded-value">
              {formatDate(row.fileUploadedAt)}
            </Fact>
            <Fact label="Updated" testId="document-drawer-updated-value">
              {formatDate(row.updatedAt)}
            </Fact>
            <Fact label="Published" testId="document-drawer-published-value">
              {row.publishedAt ? formatDate(row.publishedAt) : 'Not yet'}
            </Fact>
            <Fact label={`Views (${range} days)`} testId="document-drawer-views-value">
              <DocumentCount
                row={row}
                count={row.views}
                testId="document-drawer-views-never-published-message"
              />
            </Fact>
            <Fact
              label={`Downloads (${range} days)`}
              testId="document-drawer-downloads-value"
            >
              <DocumentCount
                row={row}
                count={row.downloads}
                testId="document-drawer-downloads-never-published-message"
              />
            </Fact>
            {row.pendingFile ? (
              <Fact label="Replacement" testId="document-drawer-pending-file-value">
                <strong>{row.pendingFile.fileName}</strong>
                {' · '}
                {typeWord(row.pendingFile.contentType)}
                {' · '}
                {sizeInWords(row.pendingFile.sizeBytes)}
                {' · uploaded '}
                {formatDate(row.pendingFile.uploadedAt)}
                {', scanning'}
              </Fact>
            ) : null}
          </Box>

          {actions.length > 0 ? (
            <Box
              role="group"
              aria-label="Actions"
              sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}
            >
              {actions.map((action) => (
                <Button
                  key={action}
                  variant={action === 'remove' ? 'danger' : 'secondary'}
                  size="sm"
                  disabled={busy}
                  onClick={() => act(action)}
                  data-testid={`document-drawer-${action}-button`}
                >
                  {ACTION_LABELS[action]}
                </Button>
              ))}
            </Box>
          ) : null}
        </>
      )}

      <ReplaceFileDialog
        key={`replace-${replaceCount}`}
        document={row}
        open={replacing}
        onClose={() => setReplacing(false)}
      />

      <RemoveDocumentDialog
        document={row}
        open={removing}
        onCancel={() => setRemoving(false)}
        onRemoved={onClose}
      />

      <UnsavedChangesDialog
        blocked={guard.blocked || confirmingClose}
        stay={() => {
          setConfirmingClose(false);
          guard.stay();
        }}
        leave={() => {
          setConfirmingClose(false);
          if (guard.blocked) guard.leave();
          else onClose();
        }}
      />
    </Drawer>
  );
}
